import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import ShareButton from "@/components/base/ShareButton";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import useSeo from "@/hooks/useSeo";
import { allNews, formatDate, formatViews, getNewsById, getWorksByIds } from "@/lib/content";

export default function NewsDetail() {
  const { id = "" } = useParams();
  const news = getNewsById(id);

  useSeo({
    title: news ? `${news.title} | 筱田优官网` : "资讯详情 | 筱田优官网",
    description: news ? news.excerpt : "筱田优影视资讯详情。",
  });

  if (!news) {
    return (
      <div className="w-full pt-24 pb-16 px-4 md:px-8">
        <EmptyState
          icon="ri-article-line"
          title="暂无详情内容"
          description="该资讯可能已被移除或链接有误，建议返回资讯列表查看其他内容。"
          actionTo="/news"
          actionLabel="返回资讯列表"
        />
      </div>
    );
  }

  const relatedWorks = getWorksByIds(news.relatedWorkIds);
  const relatedNews = allNews
    .filter((n) => n.id !== news.id)
    .map((n) => ({
      n,
      score:
        (n.category === news.category ? 1 : 0) + n.tags.filter((t) => news.tags.includes(t)).length,
    }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map((item) => item.n);

  return (
    <div className="w-full pt-20 md:pt-24">
      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: "影视资讯", to: "/news" }, { label: news.title }]} />
      </div>

      <article className="w-full px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-medium mb-4">
            {news.category}
          </span>
          <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug">
            {news.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" />
              {formatDate(news.date)}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatViews(news.views)} 次阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line" />
              {news.tags.join(" · ")}
            </span>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mt-8">
          <div className="w-full aspect-[16/9] rounded-lg overflow-hidden bg-background-200">
            <img
              src={news.cover}
              alt={`${news.title}配图`}
              title={`${news.title} - 筱田优影视资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="max-w-3xl mx-auto mt-10">
          <div className="text-base md:text-lg text-foreground-700 leading-loose flex flex-col gap-5">
            {news.content.map((block, index) =>
              block.type === "h3" ? (
                <h3
                  key={index}
                  className="font-heading text-lg md:text-xl font-bold text-foreground-950 mt-3 pb-2 border-b border-background-200"
                >
                  {block.text}
                </h3>
              ) : (
                <p key={index}>{block.text}</p>
              ),
            )}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-10 pt-6 border-t border-background-200">
            <div className="flex flex-wrap gap-2">
              {news.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/news?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-800 text-xs hover:bg-secondary-200 transition-colors"
                >
                  #{tag}
                </Link>
              ))}
            </div>
            <ShareButton title={news.title} />
          </div>
        </div>
      </article>

      {relatedWorks.length > 0 && (
        <section className="w-full px-4 md:px-8 mt-16">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-accent-500" />
            相关作品
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {relatedWorks.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        </section>
      )}

      <section className="w-full px-4 md:px-8 mt-16">
        <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-3 mb-6">
          <span className="w-1.5 h-6 rounded-full bg-primary-500" />
          推荐阅读
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {relatedNews.map((item) => (
            <Link
              key={item.id}
              to={`/news/${item.id}`}
              className="group flex gap-3 p-3 rounded-lg border border-background-200 hover:border-primary-300 transition-colors bg-background-50"
            >
              <div className="w-24 h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
                <img
                  src={item.cover}
                  alt={`${item.title}配图`}
                  title={`${item.title} - 筱田优影视资讯`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {item.title}
                </h3>
                <span className="text-xs text-foreground-400 mt-2 inline-block">{formatDate(item.date)}</span>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-8">
          <Link
            to="/news"
            className="text-sm text-primary-600 hover:text-primary-700 transition-colors inline-flex items-center gap-1"
          >
            <i className="ri-arrow-left-line" />
            返回资讯列表
          </Link>
        </div>
      </section>
    </div>
  );
}